import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Input, Modal, Radio, Select, Spin, Tabs, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { MdOpenInNew } from 'react-icons/md';
import Button from '../../../../../components/Button';
import AmondzModal from '../../../../../components/Modal';
import {
  PROMOTION_SCHEDULE_STATUS,
  PromotionSchedule,
  PromotionScheduleCoupon,
  PromotionScheduleCreateRequest,
  PromotionScheduleEvent,
  PromotionScheduleStatus,
  PromotionScheduleTrack,
  PromotionScheduleType,
} from '../../../../../lib/api/promotionSchedule';
import { EVENT_STATUS, STATUS_LABEL, TRACK_META, TRACK_ORDER, TYPE_LABEL } from '../../constants';
import {
  useCreateScheduleMutation,
  useDeleteScheduleMutation,
  useUpdateScheduleMutation,
} from '../../hooks/usePromotionScheduleMutations';
import { promotionScheduleQuery } from '../../queries';
import { DATE_FORMAT, TIME_FORMAT, formatCouponBenefit, isAllDayPeriod, toDatePart } from '../../utils';
import { formatKst, getTodayKst, kstWallClockToIso } from '../../utils/kst';
import { ScheduleLogList } from '../ScheduleLogList';
import OpenCouponDetail from '../../../../../components/link/OpenCouponDetail';
import OpenEventDetail from '../../../../../components/link/OpenEventDetail';
import { PROMOTION_SCHEDULE_DETAIL_URL_PATH } from '../../../../../constants/service/common/urlConstants';
import { setDetailPageUrl } from '../../../../../lib/common';
import {
  StyledCouponList,
  StyledCouponSelect,
  StyledField,
  StyledFieldLabel,
  StyledFieldRow,
  StyledFooter,
  StyledForm,
  StyledHint,
  StyledModalTitle,
  StyledModalTitleAction,
  StyledOptionalMark,
  StyledRequiredMark,
} from './styles';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

type Props = {
  /** null 이면 신규 등록 모드 */
  schedule: PromotionSchedule | null;
  canUpdate: boolean;
  canDelete: boolean;
  /**
   * 헤더의 "상세 페이지 열기" 노출 여부.
   *
   * **상세 화면 자신이 이 모달을 열 때는 `false`** 를 준다 — 지금 보고 있는 페이지로
   * 가는 버튼이 생겨서, 누르면 같은 화면이 새 탭에 하나 더 열린다.
   * 모달은 자기가 어디서 열렸는지 알 수 없으므로 여는 쪽이 알려준다.
   */
  showDetailLink?: boolean;
  /**
   * 삭제가 끝난 뒤 호출된다. **상세 화면처럼 "지워진 항목을 보고 있던" 호출부**가
   * 어디로 갈지 스스로 정하게 하기 위한 것이다.
   *
   * 주지 않으면 모달만 닫는다(타임라인·리스트는 그 자리에 남아 있으면 되므로 그게 맞다).
   */
  onDeleted?: (promotionScheduleId: number) => void;
  onClose: () => void;
};

/**
 * 라벨 ↔ 컨트롤 연결용 id.
 *
 * 모달은 한 번에 하나만 열리므로 고정 문자열로 충분하다(같은 id 가 둘 생길 일이 없다).
 * 값을 상수로 묶는 이유는 라벨과 컨트롤 **두 곳에 같은 문자열**을 적어야 해서다 —
 * 한쪽만 고치면 연결이 조용히 끊기고, 화면에는 아무 변화가 없다.
 */
const FIELD_LABEL_ID = {
  TRACK: 'promotion-schedule-form-track-label',
  TITLE: 'promotion-schedule-form-title-label',
  PERIOD: 'promotion-schedule-form-period-label',
  STATUS: 'promotion-schedule-form-status-label',
  TYPE: 'promotion-schedule-form-type-label',
  ADMIN: 'promotion-schedule-form-admin-label',
  MEMO: 'promotion-schedule-form-memo-label',
} as const;

/** 기간은 입력이 둘이라 각각 id 를 갖는다 — 그룹 이름만으로는 어느 칸인지 구분되지 않는다 */
const FIELD_ID = {
  PERIOD_START: 'promotion-schedule-form-period-start',
  PERIOD_END: 'promotion-schedule-form-period-end',
} as const;

const TAB_KEY = {
  INFO: 'info',
  LOGS: 'logs',
} as const;

type FormState = {
  track: PromotionScheduleTrack;
  title: string;
  period: [Dayjs, Dayjs];
  /** 종일 항목이면 00:00:00~23:59:59 로 저장한다 (별도 컬럼 없이 경계로 판정) */
  isAllDay: boolean;
  status: PromotionScheduleStatus;
  type: PromotionScheduleType | null;
  adminId: number | null;
  /** 연결된 쿠폰 id. 순서는 의미 없다 — 비교는 집합으로 한다 */
  couponIds: number[];
  /** 연결된 기획전 id. 1:1 이라 배열이 아니다 */
  eventId: number | null;
  memo: string;
};

const TRACK_OPTIONS = TRACK_ORDER.map((track) => ({
  value: track,
  label: TRACK_META[track].label,
}));

const TYPE_OPTIONS = (Object.keys(TYPE_LABEL) as PromotionScheduleType[]).map((type) => ({
  value: type,
  label: TYPE_LABEL[type],
}));

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as PromotionScheduleStatus[]).map((status) => ({
  value: status,
  label: STATUS_LABEL[status],
}));

/** 검색어 입력이 멈춘 뒤 질의할 때까지 (타이핑마다 요청하지 않는다) */
const COUPON_SEARCH_DEBOUNCE_MS = 300;

/**
 * 픽커 값을 KST wall-clock 문자열로 옮길 때 쓰는 포맷.
 *
 * **초까지 포함한다.** 픽커 UI 는 분 단위지만 초기값(`formatKst(…, 'yyyy-MM-dd HH:mm:ss')`)이
 * 초를 보존하므로, 저장에서 `:00` 으로 절삭하면 초가 `00` 이 아닌 항목(백필분 등)을 열어
 * 메모만 바꿔도 기간 비교가 어긋나 초가 조용히 덮인다.
 */
const WALL_CLOCK_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}:ss`;

const PERIOD_MODE_OPTIONS = [
  { value: true, label: '종일' },
  { value: false, label: '시간 지정' },
];

/**
 * 시간 지정으로 전환할 때의 기본 시각.
 *
 * 하루 전체를 그대로 두면 "시간 지정"을 고른 의미가 없으므로 운영이 흔히 쓰는 구간을 채운다.
 * (종일과의 구분 자체는 초 단위로 판정하므로 — `isAllDayPeriod` — 값이 겹쳐도 모드가 뒤집히진 않는다)
 */
const TIMED_DEFAULT_START_HOUR = 10;
const TIMED_DEFAULT_END_HOUR = 22;

/**
 * 폼 상태를 서버가 받는 ISO datetime 으로 만든다.
 *
 * 종일이면 `00:00:00`~`23:59:59` 로 맞춘다 (endDate inclusive — 레포 전역 관례).
 * 밀리초를 0 으로 떨어뜨리는 이유는 MySQL datetime 이 소수초를 안 갖기 때문에
 * 응답값과 문자열이 어긋나 "변경 없음"을 변경으로 오판하는 걸 막기 위함이다.
 */
const buildPeriod = (form: FormState): { startDate: string; endDate: string } => {
  const [start, end] = form.period;

  if (form.isAllDay) {
    return {
      startDate: kstWallClockToIso(`${start.format(DATE_FORMAT)} 00:00:00`),
      endDate: kstWallClockToIso(`${end.format(DATE_FORMAT)} 23:59:59`),
    };
  }

  return {
    startDate: kstWallClockToIso(start.format(WALL_CLOCK_FORMAT)),
    endDate: kstWallClockToIso(end.format(WALL_CLOCK_FORMAT)),
  };
};

const getInitialState = (schedule: PromotionSchedule | null): FormState => {
  if (!schedule) {
    return {
      track: TRACK_ORDER[0],
      title: '',
      // 신규 등록의 기본 날짜도 KST 오늘이다 (해외 접속 시 하루 밀리지 않게)
      period: [dayjs(getTodayKst()).startOf('day'), dayjs(getTodayKst()).endOf('day')],
      isAllDay: true,
      status: PROMOTION_SCHEDULE_STATUS.PLANNED,
      type: null,
      adminId: null,
      couponIds: [],
      eventId: null,
      memo: '',
    };
  }

  return {
    track: schedule.track,
    title: schedule.title,
    /**
     * 픽커에는 **KST wall-clock** 을 넣는다 — 저장값은 UTC ISO 라 로컬로 그대로 파싱하면
     * 해외 접속 시 다른 날짜·시각이 보이고, 그 값을 저장하면 종일 판정까지 뒤집힌다.
     */
    period: [
      dayjs(formatKst(schedule.startDate, `yyyy-MM-dd HH:mm:ss`)),
      dayjs(formatKst(schedule.endDate, `yyyy-MM-dd HH:mm:ss`)),
    ],
    isAllDay: isAllDayPeriod(schedule.startDate, schedule.endDate),
    status: schedule.status,
    type: schedule.type,
    adminId: schedule.adminId,
    couponIds: (schedule.coupons ?? []).map((coupon) => coupon.id),
    eventId: schedule.eventId,
    memo: schedule.memo ?? '',
  };
};

/** 순서를 무시한 id 집합 비교 — 순서만 다른 걸 변경으로 보내면 이력이 지저분해진다 */
const isSameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length &&
  [...a].sort((x, y) => x - y).every((id, index) => id === [...b].sort((x, y) => x - y)[index]);

/**
 * 쿠폰이 스케줄 기간을 **다 덮지 못하는지**.
 *
 * 서버는 기간을 검증하지 않는다 — 쿠폰을 하루 먼저 열어두는 운영이 흔해서, 막으면 정상 운영이
 * 실패로 보인다. 대신 눈에 띄게만 해서 사람이 판단하게 한다.
 *
 * ⚠️ "완전히 안 겹칠 때"가 아니라 **덮지 못할 때**를 본다. 쿠폰 9/1~9/5, 스케줄 9/4~9/10 이면
 * 기획전 후반에 쿠폰이 만료돼 실제로 문제인데 겹치기는 하므로, 겹침만 보면 놓친다.
 * 반대로 쿠폰이 스케줄보다 **넓은 것은 정상**이라 잡히지 않는다.
 *
 * 비교는 표시와 같은 KST 날짜 기준이다 — 쿠폰 기간은 ISO datetime 이라 시각까지 비교하면
 * 같은 날인데도 어긋난 것으로 잡힌다.
 */
const isCouponPeriodMismatched = (coupon: PromotionScheduleCoupon, period: [Dayjs, Dayjs]): boolean => {
  if (!coupon.startDate || !coupon.endDate) {
    return false;
  }

  const [start, end] = period;
  const couponStart = dayjs(toDatePart(coupon.startDate));
  const couponEnd = dayjs(toDatePart(coupon.endDate));

  return couponStart.isAfter(start, 'day') || couponEnd.isBefore(end, 'day');
};

/**
 * 쿠폰 기간 — 스케줄 기간과 견줘보라고 보여준다.
 *
 * ⚠️ 날짜가 없다고 무기한인 것은 아니다. 쿠폰 기간 타입은 `fixedDate`·`generatedDate`·`infinity`
 * 셋인데 절대 날짜(`date`)는 `fixedDate` 에만 있다. 즉 여기 null 로 오는 것에는 **발급일 기준
 * N일** 쿠폰도 섞여 있어서, "무제한" 이라고 적으면 기간을 견주라고 만든 목록이 틀린 단서를 준다.
 *
 * 서버 join(`PromotionScheduleCoupon`)에 `periodType`·`dayPlus` 가 없어 지금은 구분할 수 없으므로,
 * 단정하지 않는 문구로 둔다. 백엔드가 두 필드를 실어주면 위젯(`getCouponPeriodText`)과 같은
 * `발급일 기준으로 N일` / `무기한` 표기로 맞춘다.
 */
const formatCouponPeriod = (coupon: PromotionScheduleCoupon): string => {
  if (!coupon.startDate || !coupon.endDate) {
    return '날짜 지정 없음';
  }

  return `${toDatePart(coupon.startDate)} ~ ${toDatePart(coupon.endDate)}`;
};

/**
 * 기획전 기간 — 스케줄 기간과 견줘보라고 보여준다.
 *
 * 무기한 노출 기획전은 날짜가 없다(`status` 2). 그 경우 비교할 기준이 없으므로 그렇게 적는다.
 */
const formatEventPeriod = (event: PromotionScheduleEvent): string => {
  if (event.status === EVENT_STATUS.UNLIMITED) {
    return '무기한 노출';
  }

  if (!event.startDate || !event.endDate) {
    return '기간 지정 없음';
  }

  return `${toDatePart(event.startDate)} ~ ${toDatePart(event.endDate)}`;
};

/**
 * 기획전이 스케줄 기간을 **다 덮지 못하는지**. 판정 규약은 쿠폰과 같다 —
 * 겹침이 아니라 덮는지를 보고, 표시와 같은 KST 날짜로 비교한다.
 *
 * 서버는 기간을 검증하지 않는다(설계). 화면 강조로만 알린다.
 *
 * ⚠️ **무기한 노출은 날짜보다 상태가 먼저다.** 목록 API 는 `status: 'unlimited'` 이면서도
 * `startDate`·`endDate` 를 함께 주는 경우가 있어, 날짜 유무만 보면 끝나지 않는 기획전에
 * "기간 불일치" 경고가 붙는다.
 */
const isEventPeriodMismatched = (event: PromotionScheduleEvent, period: [Dayjs, Dayjs]): boolean => {
  if (event.status === EVENT_STATUS.UNLIMITED || !event.startDate || !event.endDate) {
    return false;
  }

  const [start, end] = period;

  return (
    dayjs(toDatePart(event.startDate)).isAfter(start, 'day') || dayjs(toDatePart(event.endDate)).isBefore(end, 'day')
  );
};

/**
 * 캐시에 담긴 기획전이 새로 받은 것과 같은 내용인지.
 *
 * id 가 같아도 제목·기간·상태가 바뀔 수 있어(저장 후 재조회 등) 값으로 비교한다.
 * id 만 보고 건너뛰면 선택 표시와 기간 경고가 옛 값으로 남는다.
 */
const isSameEvent = (a: PromotionScheduleEvent, b: PromotionScheduleEvent): boolean =>
  a.mainTitle === b.mainTitle && a.startDate === b.startDate && a.endDate === b.endDate && a.status === b.status;

/** 연결된 쿠폰을 select 옵션 모양으로 — 검색 결과에 없어도 선택 상태를 유지하려면 필요하다 */
const toCouponOption = (coupon: PromotionScheduleCoupon) => ({
  value: coupon.id,
  label: `${coupon.name} · ${formatCouponBenefit(coupon)}`,
  coupon,
});

/**
 * 스케줄 등록·수정 모달.
 *
 * 수정 시 전체 필드를 보내지 않고 **바뀐 필드만** PUT 한다 — 서버가 부분 수정이라
 * 전체 전송은 다른 사람이 동시에 바꾼 값을 되돌릴 위험이 있다.
 */
export const ScheduleFormModal = (props: Props): React.JSX.Element => {
  const { schedule, canUpdate, canDelete, showDetailLink = true, onDeleted, onClose } = props;

  const isEditMode = schedule !== null;
  const [form, setForm] = useState<FormState>(() => getInitialState(schedule));
  const [activeTab, setActiveTab] = useState<string>(TAB_KEY.INFO);

  const { data: managers = [] } = useQuery({ ...promotionScheduleQuery.managers, staleTime: 60_000 });

  const createMutation = useCreateScheduleMutation();
  const updateMutation = useUpdateScheduleMutation();
  const deleteMutation = useDeleteScheduleMutation();

  useEffect(() => {
    setForm(getInitialState(schedule));
  }, [schedule]);

  const managerOptions = useMemo(
    () =>
      managers.map((manager) => ({
        value: manager.id,
        label: manager.department ? `${manager.name} (${manager.department})` : manager.name,
      })),
    [managers],
  );

  /**
   * 쿠폰 검색 — 전체가 5천 건대라 목록을 다 불러오지 않고 **이름으로 좁혀** 고른다.
   * 입력이 멈춘 뒤에만 질의한다(타이핑마다 요청하면 목록이 깜빡이며 뒤바뀐다).
   */
  const [couponKeyword, setCouponKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(couponKeyword.trim()), COUPON_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [couponKeyword]);

  const { data: couponSearch, isFetching: isCouponSearching } = useQuery({
    ...promotionScheduleQuery.coupons(debouncedKeyword),
    staleTime: 60_000,
  });

  /**
   * 한 번이라도 본 쿠폰을 id → 객체로 쌓아둔다.
   *
   * ⚠️ 검색 결과(`couponSearch`)와 서버가 준 연결(`schedule.coupons`)만으로 해석하면,
   * **신규 등록에서 고른 쿠폰이 검색어를 바꾸는 순간 사라진다** — `schedule` 이 없고 검색 결과도
   * 갈렸기 때문이다. 그러면 `couponIds` 에는 id 가 남는데 화면에는 안 보여
   * **해제도 못 하고 그대로 저장된다.** 그래서 별도 캐시에 보존한다.
   */
  const [couponCache, setCouponCache] = useState<Map<number, PromotionScheduleCoupon>>(new Map());

  useEffect(() => {
    const incoming = [...(schedule?.coupons ?? []), ...(couponSearch ?? [])];

    if (!incoming.length) {
      return;
    }

    setCouponCache((prev) => {
      const next = new Map(prev);
      let added = false;

      incoming.forEach((coupon) => {
        if (!next.has(coupon.id)) {
          next.set(coupon.id, coupon);
          added = true;
        }
      });

      // 새로 담긴 게 없으면 같은 참조를 돌려준다 — 매번 새 Map 을 만들면 effect 가 무한히 돈다
      return added ? next : prev;
    });
  }, [couponSearch, schedule]);

  const selectedCoupons = useMemo(
    () =>
      form.couponIds
        .map((id) => couponCache.get(id))
        .filter((coupon): coupon is PromotionScheduleCoupon => coupon !== undefined),
    [form.couponIds, couponCache],
  );

  const [eventKeyword, setEventKeyword] = useState('');
  const [debouncedEventKeyword, setDebouncedEventKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEventKeyword(eventKeyword.trim()), COUPON_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [eventKeyword]);

  const { data: eventSearch, isFetching: isEventSearching } = useQuery({
    ...promotionScheduleQuery.events(debouncedEventKeyword),
    staleTime: 60_000,
  });

  /** 쿠폰 캐시와 같은 이유 — 검색어를 바꿔도 고른 기획전이 화면에서 사라지지 않게 보존한다 */
  const [eventCache, setEventCache] = useState<Map<number, PromotionScheduleEvent>>(new Map());

  useEffect(() => {
    const incoming = [...(schedule?.event ? [schedule.event] : []), ...(eventSearch ?? [])];

    /**
     * 연결은 남았는데 기획전이 지워진 상태 — 서버가 `eventId` 는 주고 `event` 만 null 로 준다.
     *
     * 캐시에 옛 정보가 남아 있으면 지워진 기획전을 멀쩡한 것처럼 그리고,
     * 그 아래 "정보를 불러오지 못했습니다" 안내까지 가려진다. 그 id 만 비운다 —
     * 다른 id 는 검색어가 바뀌어도 선택을 잃지 않게 그대로 둔다.
     */
    const staleEventId = schedule && schedule.eventId !== null && !schedule.event ? schedule.eventId : null;

    if (!incoming.length && staleEventId === null) {
      return;
    }

    setEventCache((prev) => {
      const next = new Map(prev);
      let changed = staleEventId !== null && next.delete(staleEventId);

      incoming.forEach((event) => {
        const cached = next.get(event.id);

        if (!cached || !isSameEvent(cached, event)) {
          next.set(event.id, event);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [eventSearch, schedule]);

  const selectedEvent = useMemo(
    () => (form.eventId === null ? null : eventCache.get(form.eventId) ?? null),
    [form.eventId, eventCache],
  );

  /** 쿠폰과 같은 이유로 **검색 결과만** 담는다 (아래 주석 참고) */
  /**
   * 옵션은 **검색 결과만** 담는다 — 쿠폰과 같은 이유다(아래 `couponOptions` 주석).
   *
   * ⚠️ 그 대가로 **선택한 기획전이 옵션에 없을 때 라벨이 깨진다.** rc-select 는 옵션에 없는
   * 값의 라벨을 `item.label ?? item.value` 로 채우므로(`rc-select/Select.js`), 제목 대신
   * `4855` 같은 id 숫자가 그려진다. 수정 모드로 열었을 때 연결된 기획전이 기본 검색 50건에
   * 없거나, 고른 뒤 blur 로 검색어가 리셋되면 바로 재현된다.
   *
   * 쿠폰은 `mode="multiple"` + `tagRender` 로 표시 자체를 감춰서 이 문제가 없지만,
   * 단일 Select 에는 그 장치가 없다. 그래서 `labelRender` 로 라벨만 바로잡는다 —
   * 옵션에 선택 항목을 섞는 방법은 위 함정(`notFoundContent` 미렌더·오클릭 해제)을 부른다.
   */
  const eventOptions = useMemo(
    () =>
      (eventSearch ?? []).map((event) => ({
        value: event.id,
        label: event.mainTitle,
      })),
    [eventSearch],
  );

  /**
   * 옵션은 **검색 결과만** 담는다.
   *
   * ⚠️ 연결된 쿠폰을 여기 섞으면 안 된다. `filterOption={false}` 라 rc-select 는 필터를 통째로
   * 우회하고 빈 상태를 `옵션 수 === 0` 으로만 판정한다. 그래서 연결이 하나라도 있으면
   * `notFoundContent`(빈 상태·로딩 스피너)가 영영 렌더되지 않고, 검색어와 무관한 그 항목이
   * 드롭다운에 남는다. 게다가 이미 선택 상태라 **누르면 연결이 해제된다** — 사용자는
   * "검색 결과를 골랐다"고 생각하는데 반대로 동작한다.
   *
   * 선택 유지는 `value` 가 controlled 라 옵션에 없어도 깨지지 않고, 연결 목록은 아래에서
   * `selectedCoupons`(couponCache 기반)로 따로 그린다.
   */
  const couponOptions = useMemo(() => (couponSearch ?? []).map(toCouponOption), [couponSearch]);

  /** 수정 diff 비교용 — 서버가 준 연결 상태 */
  const currentCouponIds = useMemo(() => (schedule?.coupons ?? []).map((coupon) => coupon.id), [schedule]);

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  const patchForm = <Key extends keyof FormState>(key: Key, value: FormState[Key]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleAllDayChange = (isAllDay: boolean) => {
    // 기간은 갱신자 안에서 prev 로 읽는다 — 클로저의 form 과 섞으면 같은 렌더에서
    // 다른 setForm 이 먼저 큐잉될 때 낡은 값이 반영된다
    setForm((prev) => {
      const [start, end] = prev.period;

      return {
        ...prev,
        isAllDay,
        period: isAllDay
          ? [start.startOf('day'), end.endOf('day')]
          : [
              start.hour(TIMED_DEFAULT_START_HOUR).minute(0).second(0),
              end.hour(TIMED_DEFAULT_END_HOUR).minute(0).second(0),
            ],
      };
    });
  };

  const handleSubmit = async () => {
    const title = form.title.trim();

    if (!title) {
      message.warning('항목명을 입력해주세요.');
      return;
    }

    const { startDate, endDate } = buildPeriod(form);

    if (endDate < startDate) {
      message.warning('종료일시는 시작일시보다 빠를 수 없습니다.');
      return;
    }

    if (!isEditMode) {
      try {
        await createMutation.mutateAsync({
          track: form.track,
          title,
          startDate,
          endDate,
          status: form.status,
          type: form.type,
          adminId: form.adminId,
          couponIds: form.couponIds,
          eventId: form.eventId,
          memo: form.memo.trim() || null,
        });
      } catch {
        // 실패 문구는 SuperFetchV2 인터셉터가 에러 모달로 띄운다. 여기서 삼키지 않으면
        // 이벤트 핸들러 경계라 처리되지 않은 rejection 이 되고 모달도 닫히지 않는다.
        return;
      }

      message.success('프로모션을 등록했습니다.');
      onClose();
      return;
    }

    // 바뀐 필드만 추린다 (부분 수정)
    const body: Partial<PromotionScheduleCreateRequest> = {};
    const nextMemo = form.memo.trim() || null;

    if (form.track !== schedule.track) body.track = form.track;
    if (title !== schedule.title) body.title = title;
    // 문자열이 아니라 시각으로 비교한다 — 서버 응답과 표기 형식이 달라도 같은 시각이면 안 보낸다
    if (!dayjs(startDate).isSame(schedule.startDate, 'second')) body.startDate = startDate;
    if (!dayjs(endDate).isSame(schedule.endDate, 'second')) body.endDate = endDate;
    if (form.status !== schedule.status) body.status = form.status;
    if (form.type !== schedule.type) body.type = form.type;
    if (form.adminId !== schedule.adminId) body.adminId = form.adminId;
    // 쿠폰은 순서가 의미 없으므로 정렬해서 비교한다 — 순서만 다른 걸 변경으로 보내면 이력이 지저분해진다
    if (!isSameIdSet(form.couponIds, currentCouponIds)) body.couponIds = form.couponIds;
    if (form.eventId !== schedule.eventId) body.eventId = form.eventId;
    if (nextMemo !== (schedule.memo ?? null)) body.memo = nextMemo;

    if (Object.keys(body).length === 0) {
      message.info('변경된 내용이 없습니다.');
      return;
    }

    try {
      await updateMutation.mutateAsync({ id: schedule.id, body });
    } catch {
      return;
    }

    message.success('프로모션을 수정했습니다.');
    onClose();
  };

  const handleDelete = () => {
    if (!schedule) {
      return;
    }

    Modal.confirm({
      title: '프로모션 삭제',
      content: '삭제하면 타임라인·리스트에서 사라집니다. 취소 이력을 남기려면 상태를 "취소"로 변경하세요.',
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '닫기',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(schedule.id);
        } catch {
          // 실패 문구는 SuperFetchV2 인터셉터가 띄운다. 여기서 삼키지 않으면
          // 에러 모달과 confirm 이 겹쳐 뜬다 (handleSubmit 과 같은 패턴).
          return;
        }

        message.success('프로모션을 삭제했습니다.');
        onClose();

        /**
         * 닫은 **뒤에** 알린다 — 상세 화면은 이 콜백에서 리스트로 떠나는데,
         * 모달이 아직 떠 있는 상태로 화면을 바꾸면 모달이 남은 채 배경만 갈린다.
         */
        onDeleted?.(schedule.id);
      },
    });
  };

  const isReadOnly = isEditMode && !canUpdate;
  const isLogsTab = activeTab === TAB_KEY.LOGS;
  // 이력 탭에서는 저장/삭제가 맥락에 안 맞으므로 감춘다
  const showEditActions = !isLogsTab;

  const formContent = (
    <StyledForm>
      {/* 표시가 무슨 뜻인지 한 줄로 못 박는다 — 별표만 있으면 "빨간 건 뭐지" 가 된다 */}
      <StyledHint>
        <StyledRequiredMark aria-hidden="true">*</StyledRequiredMark> 표시는 필수 항목입니다.
      </StyledHint>
      {/* 트랙은 값이 짧고 항목명은 길다 — 한 줄로 묶어 세로를 줄인다 */}
      <StyledFieldRow $ratio="200px 1fr">
        <StyledField>
          {/*
            라벨은 `label` 이지만 antd 컨트롤을 감싸지 않아 `htmlFor` 가 실제 입력에 닿지 않는다
            (antd 가 내부에 input 을 따로 그린다). 그래서 라벨에 id 를 주고 컨트롤이
            `aria-labelledby` 로 가리킨다 — 보조 기술이 이름을 읽을 수 있는 유일한 연결이다.
            별표는 `aria-hidden` 이라 소리로는 안 들리므로 필수는 `aria-required` 로 따로 알린다.
          */}
          <StyledFieldLabel id={FIELD_LABEL_ID.TRACK}>
            <StyledRequiredMark aria-hidden="true">*</StyledRequiredMark>
            트랙
          </StyledFieldLabel>
          <Select
            value={form.track}
            options={TRACK_OPTIONS}
            disabled={isReadOnly}
            aria-labelledby={FIELD_LABEL_ID.TRACK}
            aria-required={true}
            onChange={(value) => patchForm('track', value)}
          />
        </StyledField>

        <StyledField>
          <StyledFieldLabel id={FIELD_LABEL_ID.TITLE}>
            <StyledRequiredMark aria-hidden="true">*</StyledRequiredMark>
            항목명
          </StyledFieldLabel>
          <Input
            value={form.title}
            maxLength={255}
            placeholder="예: 썸머 세일"
            disabled={isReadOnly}
            aria-labelledby={FIELD_LABEL_ID.TITLE}
            aria-required={true}
            onChange={(event) => patchForm('title', event.target.value)}
          />
        </StyledField>
      </StyledFieldRow>

      {/*
        기간은 컨트롤이 둘(종일/시간 지정 + 날짜 범위)이라 그룹으로 묶어 이름을 한 번만 준다.

        ⚠️ **그룹에는 `aria-required` 를 걸지 않는다** — ARIA 가 `role="group"` 에서 지원하지 않는
        조합이라 보조 기술이 무시한다. 필수는 값을 실제로 받는 **입력 각각**에 걸어야 한다
        (아래 `RangePicker` 의 `aria-required`).
      */}
      <StyledField role="group" aria-labelledby={FIELD_LABEL_ID.PERIOD}>
        <StyledFieldLabel id={FIELD_LABEL_ID.PERIOD}>
          <StyledRequiredMark aria-hidden="true">*</StyledRequiredMark>
          기간
        </StyledFieldLabel>
        <Radio.Group
          aria-label="기간 입력 방식"
          value={form.isAllDay}
          disabled={isReadOnly}
          optionType="button"
          buttonStyle="solid"
          size="small"
          options={PERIOD_MODE_OPTIONS}
          onChange={(event) => handleAllDayChange(event.target.value)}
        />
        <RangePicker
          value={form.period}
          allowClear={false}
          disabled={isReadOnly}
          /**
           * `rc-picker` 가 `aria-required` 를 **시작·종료 입력 각각에** 흘려준다
           * (`RangeSelector` 의 `getInputProps`). 그룹에 걸 수 없는 필수 상태가 여기서 전달된다.
           * 두 입력은 이름도 따로 필요해 `id` 를 start/end 로 나눠 준다.
           */
          aria-required={true}
          id={{ start: FIELD_ID.PERIOD_START, end: FIELD_ID.PERIOD_END }}
          showTime={form.isAllDay ? false : { format: TIME_FORMAT, minuteStep: 5 }}
          format={form.isAllDay ? DATE_FORMAT : `${DATE_FORMAT} ${TIME_FORMAT}`}
          onChange={(value) => {
            if (value?.[0] && value?.[1]) {
              patchForm('period', [value[0], value[1]]);
            }
          }}
        />
        <StyledHint>
          {form.isAllDay
            ? '종료일 포함. 3·5·7·10일 등 자유롭게 지정할 수 있습니다.'
            : '타임딜처럼 시간 단위로 진행하는 항목에 사용합니다. 종료 시각까지 포함됩니다.'}
        </StyledHint>
      </StyledField>

      {/* 상태·유형·담당자는 전부 짧은 select 다 — 세 개를 한 줄에 */}
      <StyledFieldRow $ratio="1fr 1fr 1fr">
        <StyledField>
          <StyledFieldLabel id={FIELD_LABEL_ID.STATUS}>
            상태
            <StyledOptionalMark>선택</StyledOptionalMark>
          </StyledFieldLabel>
          <Select
            value={form.status}
            options={STATUS_OPTIONS}
            disabled={isReadOnly}
            aria-labelledby={FIELD_LABEL_ID.STATUS}
            onChange={(value) => patchForm('status', value)}
          />
        </StyledField>

        <StyledField>
          <StyledFieldLabel id={FIELD_LABEL_ID.TYPE}>
            기획전 유형
            <StyledOptionalMark>선택</StyledOptionalMark>
          </StyledFieldLabel>
          <Select
            value={form.type}
            options={TYPE_OPTIONS}
            allowClear={true}
            placeholder="미지정"
            disabled={isReadOnly}
            aria-labelledby={FIELD_LABEL_ID.TYPE}
            onChange={(value) => patchForm('type', value ?? null)}
          />
        </StyledField>

        <StyledField>
          <StyledFieldLabel id={FIELD_LABEL_ID.ADMIN}>
            담당자
            <StyledOptionalMark>선택</StyledOptionalMark>
          </StyledFieldLabel>
          <Select
            value={form.adminId}
            options={managerOptions}
            allowClear={true}
            showSearch={true}
            optionFilterProp="label"
            placeholder="미지정"
            disabled={isReadOnly}
            aria-labelledby={FIELD_LABEL_ID.ADMIN}
            onChange={(value) => patchForm('adminId', value ?? null)}
          />
        </StyledField>
      </StyledFieldRow>

      <StyledHint>미확정은 타임라인에서 회색으로 표시됩니다.</StyledHint>

      <StyledField>
        <StyledFieldLabel>
          쿠폰 {selectedCoupons.length > 0 && `(${selectedCoupons.length})`}
          <StyledOptionalMark>선택</StyledOptionalMark>
        </StyledFieldLabel>
        <StyledCouponSelect>
          <Select
            mode="multiple"
            aria-label="쿠폰 검색"
            value={form.couponIds}
            options={couponOptions}
            filterOption={false}
            showSearch={true}
            allowClear={false}
            placeholder="쿠폰명으로 검색해 추가"
            disabled={isReadOnly}
            notFoundContent={isCouponSearching ? <Spin size="small" /> : '검색 결과가 없습니다.'}
            /**
             * 태그를 그리지 않는다 — 연결 상태는 아래 목록이 전담한다.
             * CSS 로 감추면 antd 업그레이드 때 조용히 되살아나므로 공식 API 를 쓴다.
             */
            tagRender={() => <></>}
            onSearch={setCouponKeyword}
            onChange={(value) => patchForm('couponIds', value)}
            onBlur={() => setCouponKeyword('')}
          />
        </StyledCouponSelect>
        {/*
          rc-select 는 선택이 있으면 placeholder 를 그리지 않고 닫힌 상태의 input 도 보이지 않는다.
          태그까지 감췄으니 쿠폰을 고른 뒤 이 박스는 빈칸이 된다 — 무엇을 하는 칸인지 남겨둔다.
        */}
        <StyledHint>쿠폰명으로 검색해 추가합니다.</StyledHint>
        {selectedCoupons.length > 0 && (
          <StyledCouponList>
            {selectedCoupons.map((coupon) => (
              <li key={coupon.id}>
                <span className="benefit">{formatCouponBenefit(coupon)}</span>
                <span className="name">
                  <OpenCouponDetail text={`#${coupon.id}`} couponId={coupon.id} />
                  <span className="title">{coupon.name}</span>
                </span>
                <span className={`period${isCouponPeriodMismatched(coupon, form.period) ? ' is-mismatched' : ''}`}>
                  {formatCouponPeriod(coupon)}
                </span>
                <button
                  type="button"
                  className="remove"
                  aria-label={`${coupon.name} 연결 해제`}
                  disabled={isReadOnly}
                  onClick={() =>
                    patchForm(
                      'couponIds',
                      form.couponIds.filter((id) => id !== coupon.id),
                    )
                  }
                >
                  ×
                </button>
              </li>
            ))}
          </StyledCouponList>
        )}
        <StyledHint>
          admin 쿠폰 관리에서 만든 쿠폰을 연결합니다. 아직 만들지 않았다면 메모에 계획을 적어두고 나중에 연결하세요.
        </StyledHint>
      </StyledField>

      <StyledField>
        <StyledFieldLabel id={FIELD_LABEL_ID.MEMO}>
          메모
          <StyledOptionalMark>선택</StyledOptionalMark>
        </StyledFieldLabel>
        <TextArea
          value={form.memo}
          rows={3}
          disabled={isReadOnly}
          aria-labelledby={FIELD_LABEL_ID.MEMO}
          onChange={(event) => patchForm('memo', event.target.value)}
        />
      </StyledField>

      <StyledField>
        <StyledFieldLabel>
          연결된 기획전
          <StyledOptionalMark>선택</StyledOptionalMark>
        </StyledFieldLabel>
        <StyledCouponSelect>
          <Select
            aria-label="기획전 검색"
            value={form.eventId}
            options={eventOptions}
            labelRender={({ value }) => selectedEvent?.mainTitle ?? `#${value}`}
            filterOption={false}
            showSearch={true}
            allowClear={true}
            placeholder="기획전명으로 검색해 연결"
            disabled={isReadOnly}
            notFoundContent={isEventSearching ? <Spin size="small" /> : '검색 결과가 없습니다.'}
            onSearch={setEventKeyword}
            onChange={(value) => patchForm('eventId', value ?? null)}
            onBlur={() => setEventKeyword('')}
          />
        </StyledCouponSelect>
        {selectedEvent && (
          <StyledCouponList>
            <li className="is-event">
              <span className="name">
                <OpenEventDetail text={`#${selectedEvent.id}`} eventId={selectedEvent.id} />
                <span className="title">{selectedEvent.mainTitle}</span>
              </span>
              <span className={`period${isEventPeriodMismatched(selectedEvent, form.period) ? ' is-mismatched' : ''}`}>
                {formatEventPeriod(selectedEvent)}
              </span>
              <button
                type="button"
                className="remove"
                aria-label={`${selectedEvent.mainTitle} 연결 해제`}
                disabled={isReadOnly}
                onClick={() => patchForm('eventId', null)}
              >
                ×
              </button>
            </li>
          </StyledCouponList>
        )}
        {/*
          연결이 있는데 카드가 안 그려지는 경우 — 기획전이 지워졌거나 아직 캐시에 없다.
          id 라도 보여줘야 "연결은 돼 있는데 화면엔 없는" 상태를 사람이 알아챈다.
        */}
        {form.eventId !== null && !selectedEvent && (
          <StyledHint>연결된 기획전 #{form.eventId} — 정보를 불러오지 못했습니다(삭제되었을 수 있습니다).</StyledHint>
        )}
        <StyledHint>admin 기획전 관리에서 만든 기획전을 연결합니다.</StyledHint>
      </StyledField>
    </StyledForm>
  );

  return (
    <AmondzModal
      title={
        isEditMode ? (
          <StyledModalTitle>
            <span>프로모션 수정</span>
            {/*
              모달에는 URL 이 없다 — 슬랙으로 "이 프로모션 봐주세요" 를 보낼 방법이 여기뿐이다.
              새 탭으로 여는 이유: 지금 보던 타임라인을 잃지 않아야 한다(작업 중 확인이 대부분이다).
            */}
            {showDetailLink && (
              <StyledModalTitleAction
                href={setDetailPageUrl(PROMOTION_SCHEDULE_DETAIL_URL_PATH, schedule.id)}
                target="_blank"
                rel="noreferrer"
                title="상세 페이지 열기 (새 탭)"
                aria-label="상세 페이지 열기"
              >
                <MdOpenInNew />
              </StyledModalTitleAction>
            )}
          </StyledModalTitle>
        ) : (
          '프로모션 등록'
        )
      }
      isOpen={true}
      onClose={onClose}
      width={560}
      padding="0 24px 8px"
      destroyOnClose={true}
      footer={
        <StyledFooter>
          {isEditMode && canDelete && showEditActions && (
            <button type="button" className="delete-button" disabled={isSubmitting} onClick={handleDelete}>
              삭제
            </button>
          )}
          <div className="footer-origin">
            {showEditActions && (
              <Button
                color="primary"
                text={isEditMode ? '수정' : '등록'}
                disabled={isReadOnly || isSubmitting}
                onClick={handleSubmit}
              />
            )}
            <Button text="닫기" onClick={onClose} />
          </div>
        </StyledFooter>
      }
    >
      {/* 이력 탭은 별도 권한을 두지 않는다 — 타임라인(readList)을 볼 수 있으면 이력도 본다 */}
      {isEditMode ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: TAB_KEY.INFO, label: '정보', children: formContent },
            {
              key: TAB_KEY.LOGS,
              label: '변경 이력',
              children: <ScheduleLogList promotionScheduleId={schedule.id} />,
            },
          ]}
        />
      ) : (
        formContent
      )}
    </AmondzModal>
  );
};
